import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import { Check, ArrowRight } from "lucide-react";

const TRIAL_URL = "https://6vmun24i.forms.app/plumbing-lead-qualification-form";

const features = [
  "AI Speed-to-Lead texting",
  "Missed call auto-response",
  "Lead qualification & filtering",
  "Automatic appointment booking",
  "Post-job review collection",
  "Google Maps review pushing",
  "Full setup & onboarding",
  "Dedicated support",
  "Full CRM integration",
  "Live updates & reports sent to your phone",
];

export default function PricingSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section
      id="pricing"
      data-testid="pricing-section"
      className="relative py-24 md:py-32 px-6 md:px-12"
      style={{ backgroundColor: "#0D0D0D" }}
    >
      <div className="max-w-7xl mx-auto" ref={ref}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
          className="text-center mb-16"
        >
          <span
            className="inline-flex items-center gap-2 text-sm uppercase tracking-widest text-[#5B8DEF] font-medium mb-4"
            style={{ fontFamily: "Manrope, sans-serif" }}
            data-testid="pricing-label"
          >
            <span className="w-6 h-px bg-[#5B8DEF]"></span>
            Pricing
            <span className="w-6 h-px bg-[#5B8DEF]"></span>
          </span>
          <h2
            className="text-3xl md:text-4xl tracking-tight font-semibold text-white"
            style={{ fontFamily: "Outfit, sans-serif" }}
            data-testid="pricing-headline"
          >
            Simple. Transparent. No Surprises.
          </h2>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="max-w-xl mx-auto"
        >
          <div
            data-testid="pricing-card"
            className="relative bg-[#121212] border border-white/[0.08] rounded-lg overflow-hidden"
          >
            {/* Accent top border */}
            <div className="h-px bg-gradient-to-r from-transparent via-[#5B8DEF] to-transparent"></div>

            <div className="p-8 md:p-10">
              <div className="text-center mb-8">
                <span
                  className="text-sm text-[#5B8DEF] font-medium uppercase tracking-widest mb-4 block"
                  style={{ fontFamily: "Manrope, sans-serif" }}
                >
                  All-In-One Plan
                </span>

                <div className="flex items-center justify-center gap-3 mb-2">
                  <span
                    className="text-5xl md:text-6xl font-bold text-white"
                    style={{ fontFamily: "Outfit, sans-serif" }}
                  >
                    $249
                  </span>
                  <span
                    className="text-[#A3A3A3] text-base"
                    style={{ fontFamily: "Manrope, sans-serif" }}
                  >
                    /month
                  </span>
                </div>

                <p
                  className="text-[#A3A3A3] text-sm"
                  style={{ fontFamily: "Manrope, sans-serif" }}
                >
                  + $449 one-time setup fee
                </p>
              </div>

              {/* Divider */}
              <div className="h-px bg-white/[0.06] mb-8"></div>

              {/* Features */}
              <ul className="space-y-4 mb-10">
                {features.map((feature) => (
                  <li key={feature} className="flex items-start gap-3">
                    <div className="w-5 h-5 rounded-full bg-[#5B8DEF]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-3 h-3 text-[#5B8DEF]" />
                    </div>
                    <span
                      className="text-sm text-[#A3A3A3]"
                      style={{ fontFamily: "Manrope, sans-serif" }}
                    >
                      {feature}
                    </span>
                  </li>
                ))}
              </ul>

              {/* CTA */}
              <a
                href={TRIAL_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="pricing-cta"
                className="group w-full flex items-center justify-center gap-2.5 bg-[#5B8DEF] hover:bg-[#4A79D3] text-white font-semibold py-4 rounded-lg text-base transition-all duration-200 active:scale-[0.97]"
                style={{ fontFamily: "Manrope, sans-serif" }}
              >
                Start Free 3-Day Trial
                <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform duration-200" />
              </a>

              <p
                className="text-center text-xs text-[#A3A3A3] mt-4"
                style={{ fontFamily: "Manrope, sans-serif" }}
              >
                No credit card required. Cancel anytime.
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
